/**
 * EnvStrip.tsx — which database this screen is connected to, on every screen.
 *
 * Founder, 2026-10-26: "make them different colors - prod red and dev stage green". 2026-10-27: dev-stage
 * became a second tab inside admin.votd.io, so one browser window now flips between Prod and dev-stage.
 * The strip follows the tab you are on — the env the session itself belongs to (useAuth().env, resolved
 * from the path, or from ?env= on /login and /auth/callback) — so it can never show one colour while
 * the page reads the other database.
 *
 * The colour comes from the project ref the env's client actually connects with (getProjectInfo), never
 * from the env key or the hostname. A ref it does not recognise, or no URL at all, shows an amber warning
 * instead of guessing (Rule #65: a check that cannot tell must say so).
 */
import { useAuth } from '../lib/auth';
import { getProjectInfo, toneOf } from '../lib/supabase';

type Which = { label: string; detail: string; className: string };

function which(ref: string | null, url: string | null): Which {
  switch (toneOf(ref)) {
    case 'prod':
      return { label: 'PROD', detail: `real members · ${ref}`, className: 'bg-red-600 text-white' };
    case 'devstage':
      return { label: 'DEV-STAGE', detail: `test data only · ${ref}`, className: 'bg-green-600 text-white' };
    default: {
      const host = url ? url.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : 'none configured';
      return { label: 'UNKNOWN DATABASE', detail: host, className: 'bg-amber-500 text-near-black' };
    }
  }
}

/** `fixed` pins it over the top edge (Login); otherwise it sits in the flow above the layout. */
export function EnvStrip({ fixed = false }: { fixed?: boolean }) {
  const { env } = useAuth();
  const p = getProjectInfo(env);
  const w = which(p.ref, p.url);
  return (
    <div
      role="status"
      aria-label={`Database: ${w.label}`}
      className={`${w.className} ${fixed ? 'fixed top-0 inset-x-0 z-50' : 'flex-shrink-0'} h-7 px-3 flex items-center justify-center gap-2 text-xs font-bold tracking-wider`}
    >
      <span>{w.label}</span>
      <span className="font-normal opacity-90 truncate">{w.detail}</span>
    </div>
  );
}
